import {
  GraphQLFieldConfig,
  GraphQLNonNull,
  GraphQLID,
  GraphQLInt
} from 'graphql'
import { Context } from '../shared'
import {
  RouteCoverageFilterArg,
  routeCoverageFilterArgType
} from '../types/route-coverage-filter-args'
import { ContrastQueryParameters } from '../endpoint-helpers'
import {
  RouteCoverageOrder,
  routeCoverageOrderType,
  RouteCoverageOrderField
} from '../types/route-coverage-order'
import {
  routeCoveragePageType,
  RouteCoveragePage
} from '../types/route-coverage-page'
import {
  normalizeRoute,
  appendRouteCoverageFiltersToParams
} from '../endpoint-helpers/routes'

export interface RouteCoverageArgs {
  readonly organizationID: string
  readonly applicationID: string
  readonly offset: number
  readonly limit: number
  readonly orderBy?: RouteCoverageOrder
  readonly filters?: RouteCoverageFilterArg
}

export const routeCoverageArgs = {
  organizationID: {
    description: 'The unique UUID for the organization being queried',
    type: new GraphQLNonNull(GraphQLID)
  },
  applicationID: {
    description: 'The unique UUID for the application being queried',
    type: new GraphQLNonNull(GraphQLID)
  },
  offset: {
    description: 'The index in the collection to start at',
    type: new GraphQLNonNull(GraphQLInt)
  },
  limit: {
    description: 'The number of items to fetch',
    type: new GraphQLNonNull(GraphQLInt)
  },
  orderBy: {
    description: 'Options for ordering routes',
    type: routeCoverageOrderType
  },
  filters: {
    description: 'Options for filtering routes',
    type: routeCoverageFilterArgType
  }
}

const orderMap: { [key in RouteCoverageOrderField]: string } = {
  DISCOVERED: 'discovered',
  EXERCISED: 'exercised',
  SIGNATURE: 'signature'
}

export function appendSortToParams(
  params: ContrastQueryParameters,
  orderBy: RouteCoverageOrder | undefined
) {
  if (orderBy) {
    const { field, direction } = orderBy
    params.append(
      'sort',
      `${direction === 'DESC' ? '-' : ''}${orderMap[field]}`
    )
  }
}

export const routeCoverage: GraphQLFieldConfig<
  any,
  Context,
  RouteCoverageArgs
> = {
  description: 'Get a page of application routes',
  type: new GraphQLNonNull(routeCoveragePageType),
  args: routeCoverageArgs,
  async resolve(_source, args, context): Promise<Partial<RouteCoveragePage>> {
    const {
      organizationID: organizationUuid,
      applicationID: applicationUuid,
      offset,
      limit,
      orderBy,
      filters
    } = args
    const params = new ContrastQueryParameters()

    params.append('offset', offset)
    params.append('limit', limit)
    params.append('expand', 'skip_links,filterExceptSessionMetadata')

    appendSortToParams(params, orderBy)
    appendRouteCoverageFiltersToParams(params, filters)

    const {
      routes = [],
      count: total,
      global_count: globalTotal,
      exercised_count: exercisedCount,
      total_count_except_session_metadata_filter:
        totalCountExceptSessionMetadataFilter,
      exercised_count_except_session_metadata_filter:
        exercisedCountExceptSessionMetadataFilter
    } = await context.connector.get(
      `/${organizationUuid}/applications/${applicationUuid}/route?${params}`
    )

    return {
      total,
      items: routes.map(normalizeRoute),
      globalTotal,
      exercisedCount,
      totalCountExceptSessionMetadataFilter,
      exercisedCountExceptSessionMetadataFilter
    }
  }
}
